import React, { Component } from 'react'
import { toast } from 'react-toastify'
import { getString, getLocale } from '../../../locale'
import {
    getDrinkList,
    DrinkInfo,
    startPouring,
    stopPouring,
    getPouringStatus,
    emergencyStop,
    PouringMode,
    SweetnessLevel,
    PouringStatusResponse,
} from '../../../api/0x0005/drinks'
import { getChannelList, ChannelInfo } from '../../../api/0x0005/channels'
import { getSyrupList, SyrupInfo } from '../../../api/0x0005/drinks'
import { getCupVolume, getBottleVolume, getManualPourMode, getPouringCompletionWindow } from '../../../api/0x0005/general-settings'
import {
    HiPlay,
    HiStop,
    HiCheckCircle,
    HiClock,
    HiHand,
    HiCube,
    HiBeaker,
    HiSparkles,
    HiStar,
    HiFire,
    HiLightningBolt
} from 'react-icons/hi'
import { MdWarning } from 'react-icons/md'
import { GiCoffeeCup, GiWaterBottle, GiWaterDrop } from 'react-icons/gi'
import { BiDrink } from 'react-icons/bi'

type Props = {
    clientId: string
}

type State = {
    drinks: DrinkInfo[]
    channels: ChannelInfo[]
    syrups: Map<number, SyrupInfo>
    loading: boolean
    selectedDrink?: DrinkInfo
    pouringMode: PouringMode
    sweetnessLevel: SweetnessLevel
    isPouring: boolean
    pouringStatus?: PouringStatusResponse
    statusInterval?: NodeJS.Timeout
    completionTimeout?: NodeJS.Timeout
    cupVolume: number
    bottleVolume: number
    manualHoldEnabled: boolean
    completionWindowMs: number
    completionDeadlineAt?: number
    topOffSessionActive: boolean
}

export default class PouringTab extends Component<Props, State> {
    state: State = {
        drinks: [],
        channels: [],
        syrups: new Map(),
        loading: true,
        selectedDrink: undefined,
        pouringMode: PouringMode.CUP,
        sweetnessLevel: SweetnessLevel.REGULAR,
        isPouring: false,
        pouringStatus: undefined,
        statusInterval: undefined,
        cupVolume: 0,
        bottleVolume: 0,
        manualHoldEnabled: true,
        completionWindowMs: 3000,
        completionDeadlineAt: undefined,
        topOffSessionActive: false,
    }

    async componentDidMount() {
        await Promise.all([
            this.loadDrinks(),
            this.loadChannels(),
            this.loadSyrups(),
            this.loadCupVolume(),
            this.loadBottleVolume(),
            this.loadGeneralPouringSettings(),
        ])
    }

    componentWillUnmount() {
        if (this.state.statusInterval) {
            clearInterval(this.state.statusInterval)
        }
        if (this.state.completionTimeout) {
            clearTimeout(this.state.completionTimeout)
        }
    }

    loadDrinks = async () => {
        try {
            this.setState({ loading: true })
            const response = await getDrinkList(this.props.clientId)
            if (response.data.success === 1) {
                this.setState({ drinks: response.data.drinks.filter((drink) => drink.enabled === 1) })
            } else {
                toast.error(getString('drinks.load_error'))
            }
        } catch (error: any) {
            toast.error(error?.message || getString('drinks.load_error'))
        } finally {
            this.setState({ loading: false })
        }
    }

    loadChannels = async () => {
        try {
            const response = await getChannelList(this.props.clientId)
            if (response.data.success === 1) {
                this.setState({ channels: response.data.channels })
            }
        } catch (error: any) {
            console.error('Failed to load channels:', error)
        }
    }

    loadSyrups = async () => {
        try {
            const response = await getSyrupList(this.props.clientId)
            if (response.data.success === 1) {
                const syrupMap = new Map<number, SyrupInfo>()
                response.data.syrups.forEach(syrup => syrupMap.set(syrup.id, syrup))
                this.setState({ syrups: syrupMap })
            }
        } catch (error: any) {
            console.error('Failed to load syrups:', error)
        }
    }

    loadCupVolume = async () => {
        try {
            const response = await getCupVolume(this.props.clientId)
            this.setState({ cupVolume: response.volume })
        } catch (error: any) {
            console.error('Failed to load cup volume:', error)
        }
    }

    loadBottleVolume = async () => {
        try {
            const response = await getBottleVolume(this.props.clientId)
            this.setState({ bottleVolume: response.volume })
        } catch (error: any) {
            console.error('Failed to load bottle volume:', error)
        }
    }

    loadGeneralPouringSettings = async () => {
        try {
            const [manualMode, completionWindow] = await Promise.all([
                getManualPourMode(this.props.clientId),
                getPouringCompletionWindow(this.props.clientId).catch(() => ({ completionWindowMs: 3000 })),
            ])
            this.setState({
                manualHoldEnabled: manualMode.holdEnabled === 1,
                completionWindowMs: completionWindow?.completionWindowMs ?? 3000,
            })
        } catch (error: any) {
            console.error('Failed to load pouring settings:', error)
        }
    }

    clearCompletionTimer = () => {
        if (this.state.completionTimeout) {
            clearTimeout(this.state.completionTimeout)
            this.setState({ completionTimeout: undefined, completionDeadlineAt: undefined })
        } else if (this.state.completionDeadlineAt !== undefined) {
            this.setState({ completionDeadlineAt: undefined })
        }
    }

    startCompletionWindow = () => {
        this.clearCompletionTimer()
        const duration = Math.min(Math.max(this.state.completionWindowMs, 500), 30000)
        const completionTimeout = setTimeout(() => {
            if (!this.state.isPouring) {
                this.setState({
                    topOffSessionActive: false,
                    completionDeadlineAt: undefined,
                    completionTimeout: undefined,
                })
                if (this.state.statusInterval) {
                    clearInterval(this.state.statusInterval)
                    this.setState({ statusInterval: undefined })
                }
            }
        }, duration)
        this.setState({
            topOffSessionActive: true,
            completionDeadlineAt: Date.now() + duration,
            completionTimeout,
        })
    }

    fetchPouringStatus = async () => {
        try {
            const response = await getPouringStatus(this.props.clientId)
            if (response.data) {
                const wasPouring = this.state.isPouring
                const nowPouring = response.data.isPouring === 1
                const successCompleted = wasPouring && !nowPouring && response.data.error === 0
                const hasError = !nowPouring && response.data.error === 1

                if (successCompleted) {
                    this.startCompletionWindow()
                } else if (nowPouring) {
                    this.clearCompletionTimer()
                } else if (hasError) {
                    this.clearCompletionTimer()
                    this.setState({ topOffSessionActive: false })
                }

                this.setState({
                    pouringStatus: response.data,
                    isPouring: nowPouring
                })

                // Stop polling only after completion window session ends
                if (response.data.isPouring === 0 && !this.state.topOffSessionActive && this.state.statusInterval) {
                    clearInterval(this.state.statusInterval)
                    this.setState({ statusInterval: undefined })
                }
            }
        } catch (error: any) {
            console.error('Failed to fetch pouring status:', error)
        }
    }

    handleSelectDrink = (drink: DrinkInfo) => {
        this.setState({ selectedDrink: drink })
    }

    handleStartPouring = async () => {
        const { selectedDrink, pouringMode, sweetnessLevel } = this.state

        if (!selectedDrink) {
            toast.error(getString('pouring.select_drink_first'))
            return
        }

        try {
            const response = await startPouring(this.props.clientId, {
                drinkId: selectedDrink.id,
                mode: pouringMode,
                sweetness: sweetnessLevel,
            })

            if (response.data.success === 1) {
                toast.success(getString('pouring.started'))
                this.clearCompletionTimer()
                // Reset pouring status before starting
                this.setState({
                    isPouring: true,
                    topOffSessionActive: false,
                    pouringStatus: {
                        isPouring: 1,
                        volumeMl: 0,
                        progressPercent: 0,
                        error: 0,
                        errorMessage: ''
                    }
                })

                // Start polling for status
                const interval = setInterval(this.fetchPouringStatus, 500)
                this.setState({ statusInterval: interval })
            } else {
                toast.error(response.data.errorMsg || getString('pouring.start_error'))
            }
        } catch (error: any) {
            toast.error(error?.message || getString('pouring.start_error'))
        }
    }

    handleStopPouring = async () => {
        try {
            const response = await stopPouring(this.props.clientId)

            if (response.data.success === 1) {
                toast.success(getString('pouring.stopped'))
                this.setState({ isPouring: false })
                if (this.state.topOffSessionActive) {
                    this.startCompletionWindow()
                } else if (this.state.statusInterval) {
                    clearInterval(this.state.statusInterval)
                    this.setState({ statusInterval: undefined })
                }
            } else {
                toast.error(response.data.errorMsg || getString('pouring.stop_error'))
            }
        } catch (error: any) {
            toast.error(error?.message || getString('pouring.stop_error'))
        }
    }

    handleEmergencyStop = async () => {
        try {
            const response = await emergencyStop(this.props.clientId)

            if (response.data.success === 1) {
                toast.warning(getString('pouring.emergency_stopped'))
                this.clearCompletionTimer()
                this.setState({ isPouring: false, pouringStatus: undefined, topOffSessionActive: false })

                if (this.state.statusInterval) {
                    clearInterval(this.state.statusInterval)
                    this.setState({ statusInterval: undefined })
                }
            } else {
                toast.error(response.data.errorMsg || getString('pouring.emergency_stop_error'))
            }
        } catch (error: any) {
            toast.error(error?.message || getString('pouring.emergency_stop_error'))
        }
    }

    startTopOff = async () => {
        const { selectedDrink, sweetnessLevel } = this.state
        if (!selectedDrink) {
            toast.error(getString('pouring.select_drink_first'))
            return
        }
        try {
            this.clearCompletionTimer()
            await startPouring(this.props.clientId, {
                drinkId: selectedDrink.id,
                mode: PouringMode.MANUAL,
                sweetness: sweetnessLevel,
            })
            this.setState({ isPouring: true, topOffSessionActive: true })
            if (!this.state.statusInterval) {
                const interval = setInterval(this.fetchPouringStatus, 500)
                this.setState({ statusInterval: interval })
            }
        } catch (error: any) {
            toast.error(error?.message || getString('pouring.start_error'))
        }
    }

    stopTopOff = async () => {
        try {
            await stopPouring(this.props.clientId)
            this.setState({ isPouring: false, topOffSessionActive: true })
            this.startCompletionWindow()
        } catch (error: any) {
            toast.error(error?.message || getString('pouring.stop_error'))
        }
    }

    handleTopOffToggle = async () => {
        if (this.state.isPouring) {
            await this.stopTopOff()
            toast.success(getString('pouring.top_off_stopped'))
        } else {
            await this.startTopOff()
            toast.success(getString('pouring.top_off_active'))
        }
    }

    render() {
        const { drinks, channels, syrups, loading, selectedDrink, pouringMode, sweetnessLevel, isPouring, pouringStatus, cupVolume, bottleVolume, topOffSessionActive, completionDeadlineAt, manualHoldEnabled } = this.state
        const isHebrew = getLocale() === 'he'
        const completionSecondsLeft = completionDeadlineAt ? Math.max(0, Math.ceil((completionDeadlineAt - Date.now()) / 1000)) : 0

        // Calculate target volume based on pouring mode
        const targetVolume = pouringMode === PouringMode.CUP ? cupVolume : pouringMode === PouringMode.BOTTLE ? bottleVolume : 0

        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 p-6">
                <div className="max-w-7xl mx-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-20">
                            <div className="text-center">
                                <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
                                <p className="text-lg text-gray-600 dark:text-gray-400">{getString('common.loading')}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                            {/* Left Section - Drink Selection (Takes 2 columns) */}
                            <div className="xl:col-span-2">
                                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6">
                                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                                        <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm me-4">1</span>
                                        {getString('pouring.select_drink')}
                                    </h3>

                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                                        {drinks.map((drink) => {
                                            const displayTitle = (isHebrew && drink.titleHe) ? drink.titleHe : drink.title
                                            const isSelected = selectedDrink?.id === drink.id

                                            const baseChannel = channels.find(ch => ch.channelNum - 1 === drink.baseChannel)
                                            const baseSyrup = baseChannel ? syrups.get(baseChannel.syrupId) : undefined
                                            const baseSyrupName = baseSyrup ? (isHebrew && baseSyrup.nameHe ? baseSyrup.nameHe : baseSyrup.name) : ''

                                            return (
                                                <button
                                                    key={drink.id}
                                                    onClick={() => this.handleSelectDrink(drink)}
                                                    disabled={isPouring}
                                                    className={`relative group p-6 rounded-xl transition-all duration-200 ${isSelected
                                                            ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg ring-4 ring-blue-300 dark:ring-blue-700'
                                                            : 'bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-600 text-gray-900 dark:text-white hover:shadow-md border-2 border-gray-200 dark:border-gray-600'
                                                        } ${isPouring ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                                >
                                                    {isSelected && (
                                                        <div className="absolute top-2 right-2 bg-white text-blue-600 rounded-full p-1">
                                                            <HiCheckCircle className="w-5 h-5" />
                                                        </div>
                                                    )}
                                                    <div className="text-center">
                                                        <div className={`text-4xl mb-3 ${isSelected ? 'text-white' : 'text-blue-600 dark:text-blue-400'}`}>
                                                            <BiDrink className="w-12 h-12 mx-auto" />
                                                        </div>
                                                        <div className="font-bold text-lg mb-2 line-clamp-2">{displayTitle}</div>
                                                        {baseSyrupName && (
                                                            <div className={`text-sm ${isSelected ? 'text-blue-100' : 'text-gray-600 dark:text-gray-400'}`}>
                                                                {baseSyrupName}
                                                            </div>
                                                        )}
                                                    </div>
                                                </button>
                                            )
                                        })}
                                    </div>
                                </div>
                            </div>

                            {/* Right Section - Configuration & Control */}
                            <div className="space-y-6">
                                {/* Emergency Stop Button - Always at Top */}
                                <button
                                    onClick={this.handleEmergencyStop}
                                    className="w-full py-5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-2xl font-bold text-xl hover:from-red-700 hover:to-red-800 transition-all duration-200 shadow-xl hover:shadow-2xl"
                                >
                                    <div className="flex items-center justify-center">
                                        <MdWarning className="w-8 h-8 me-3" />
                                        {getString('pouring.emergency_stop')}
                                    </div>
                                </button>

                                {/* Pouring Mode */}
                                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6">
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                                        <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm me-4">2</span>
                                        {getString('pouring.pouring_mode')}
                                    </h3>
                                    <div className="grid grid-cols-3 gap-3">
                                        {[
                                            { mode: PouringMode.MANUAL, icon: HiHand, label: getString('pouring.manual') },
                                            { mode: PouringMode.CUP, icon: GiCoffeeCup, label: getString('pouring.cup') },
                                            { mode: PouringMode.BOTTLE, icon: GiWaterBottle, label: getString('pouring.bottle') },
                                        ].map(({ mode, icon: Icon, label }) => (
                                            <button
                                                key={mode}
                                                onClick={() => this.setState({ pouringMode: mode })}
                                                disabled={isPouring}
                                                className={`p-4 rounded-xl transition-all flex flex-col items-center justify-center ${pouringMode === mode
                                                        ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg'
                                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                                    } ${isPouring ? 'opacity-50 cursor-not-allowed' : ''}`}
                                            >
                                                <Icon className="w-8 h-8 mb-2" />
                                                <div className="text-xs font-semibold text-center">{label}</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Sweetness Level */}
                                {selectedDrink && selectedDrink.flavourChannel !== 255 && (
                                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6">
                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center">
                                            <span className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm me-4">3</span>
                                            {getString('pouring.sweetness')}
                                        </h3>
                                        <div className="grid grid-cols-2 gap-2">
                                            {[
                                                { level: SweetnessLevel.LIGHT, icon: HiSparkles, label: getString('pouring.light') },
                                                { level: SweetnessLevel.REGULAR, icon: HiStar, label: getString('pouring.regular') },
                                                { level: SweetnessLevel.SWEET, icon: HiFire, label: getString('pouring.sweet') },
                                                { level: SweetnessLevel.EXTRA_SWEET, icon: HiLightningBolt, label: getString('pouring.extra_sweet') },
                                            ].map(({ level, icon: Icon, label }) => (
                                                <button
                                                    key={level}
                                                    onClick={() => this.setState({ sweetnessLevel: level })}
                                                    disabled={isPouring}
                                                    className={`p-3 rounded-xl transition-all ${sweetnessLevel === level
                                                            ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg ring-2 ring-blue-300'
                                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                                        } ${isPouring ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    <Icon className="w-6 h-6 mx-auto mb-1" />
                                                    <div className="text-xs font-semibold">{label}</div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Pouring Status */}
                                {pouringStatus && isPouring && (
                                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-xl p-6 text-white">
                                        <h3 className="text-2xl font-bold mb-6 flex items-center">
                                            <HiClock className="w-7 h-7 me-3 animate-pulse" />
                                            {getString('pouring.status')}
                                        </h3>

                                        <div className="space-y-4">
                                            <div className="flex justify-between items-center text-lg">
                                                <span className="font-medium">{getString('pouring.volume')}</span>
                                                <span className="font-bold text-3xl">{pouringStatus.volumeMl.toFixed(0)} <span className="text-xl">ml</span></span>
                                            </div>

                                            {pouringMode !== PouringMode.MANUAL && (() => {
                                                const actualProgress = targetVolume > 0
                                                    ? Math.min((pouringStatus.volumeMl / targetVolume) * 100, 100)
                                                    : pouringStatus.progressPercent

                                                return (
                                                    <div>
                                                        <div className="flex justify-between mb-2 text-sm">
                                                            <span>{getString('pouring.progress')}</span>
                                                            <span className="font-bold">{actualProgress.toFixed(0)}%</span>
                                                        </div>
                                                        <div className="w-full bg-blue-300 rounded-full h-6 overflow-hidden">
                                                            <div
                                                                className="bg-white h-6 rounded-full transition-all duration-300 flex items-center justify-end pr-2"
                                                                style={{ width: `${actualProgress}%` }}
                                                            >
                                                                <GiWaterDrop className="text-blue-600 w-4 h-4" />
                                                            </div>
                                                        </div>
                                                        {targetVolume > 0 && (
                                                            <div className="text-xs text-center mt-1 text-blue-100">
                                                                {getString('pouring.volume')}: {pouringStatus.volumeMl.toFixed(0)} / {targetVolume} ml
                                                            </div>
                                                        )}
                                                    </div>
                                                )
                                            })()}

                                            {pouringStatus.error === 1 && (
                                                <div className="bg-red-500 text-white p-4 rounded-xl border-2 border-red-300">
                                                    <div className="flex items-center">
                                                        <HiCube className="w-6 h-6 me-2" />
                                                        <span className="font-semibold">{pouringStatus.errorMessage}</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {topOffSessionActive && (
                                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 border border-blue-200 dark:border-blue-900">
                                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{getString('pouring.completion_window')}</h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{getString('pouring.completed_waiting')}</p>
                                        <p className="text-sm text-blue-600 dark:text-blue-300 mb-4">
                                            {completionDeadlineAt
                                                ? getString('pouring.completion_seconds_left', completionSecondsLeft.toString())
                                                : getString('pouring.manual_top_off_hint')}
                                        </p>
                                        <button
                                            onClick={manualHoldEnabled ? undefined : this.handleTopOffToggle}
                                            onMouseDown={manualHoldEnabled ? this.startTopOff : undefined}
                                            onMouseUp={manualHoldEnabled ? this.stopTopOff : undefined}
                                            onMouseLeave={manualHoldEnabled && isPouring ? this.stopTopOff : undefined}
                                            onTouchStart={manualHoldEnabled ? this.startTopOff : undefined}
                                            onTouchEnd={manualHoldEnabled ? this.stopTopOff : undefined}
                                            className={`w-full py-4 rounded-xl font-bold text-lg transition-all duration-200 ${isPouring
                                                    ? 'bg-orange-500 hover:bg-orange-600 text-white'
                                                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                                                }`}
                                        >
                                            {manualHoldEnabled
                                                ? getString('pouring.hold_to_top_off')
                                                : isPouring
                                                    ? getString('pouring.stop')
                                                    : getString('pouring.manual_top_off')}
                                        </button>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                            {manualHoldEnabled
                                                ? getString('pouring.hold_to_top_off')
                                                : getString('pouring.tap_to_toggle_top_off')}
                                        </p>
                                    </div>
                                )}

                                {/* Control Button */}
                                <div>
                                    {!isPouring ? (
                                        <button
                                            onClick={this.handleStartPouring}
                                            disabled={!selectedDrink}
                                            className={`w-full py-6 rounded-2xl font-bold text-xl transition-all duration-200 shadow-xl ${selectedDrink
                                                    ? 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 hover:shadow-2xl'
                                                    : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed'
                                                }`}
                                        >
                                            <div className="flex items-center justify-center">
                                                <HiPlay className="w-8 h-8 me-3" />
                                                {getString('pouring.start')}
                                            </div>
                                        </button>
                                    ) : (
                                        <button
                                            onClick={this.handleStopPouring}
                                            className="w-full py-6 rounded-2xl font-bold text-xl transition-all duration-200 shadow-xl hover:shadow-2xl"
                                            style={{
                                                background: 'linear-gradient(to right, #f97316, #ea580c)',
                                                color: 'white'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = 'linear-gradient(to right, #ea580c, #c2410c)'
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = 'linear-gradient(to right, #f97316, #ea580c)'
                                            }}
                                        >
                                            <div className="flex items-center justify-center">
                                                <HiStop className="w-8 h-8 me-3" />
                                                {getString('pouring.stop')}
                                            </div>
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <style>{`
                    .custom-scrollbar::-webkit-scrollbar {
                        width: 8px;
                    }
                    .custom-scrollbar::-webkit-scrollbar-track {
                        background: transparent;
                    }
                    .custom-scrollbar::-webkit-scrollbar-thumb {
                        background: #cbd5e0;
                        border-radius: 4px;
                    }
                    .dark .custom-scrollbar::-webkit-scrollbar-thumb {
                        background: #4a5568;
                    }
                    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                        background: #a0aec0;
                    }
                    .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                        background: #718096;
                    }
                `}</style>
            </div>
        )
    }
}
